import { Loader2, Printer } from 'lucide-react';
import React, { useState } from 'react';
import type { AssetInstance } from '@/types/assetTemplate';
import {
  fetchAllInstancesForTemplate,
  filterInstancesByRange,
  toUnitLabelItems,
} from '../utils/fetchInstancesForLabels';
import { printUnitLabelPdf } from '../utils/unitLabelPdfGenerator';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

type PrintMode = 'selected' | 'all' | 'range';

interface PrintLabelsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  productName: string;
  templateCode: string;
  selectedInstances: AssetInstance[];
  initialMode?: PrintMode;
}

const PrintLabelsDialog: React.FC<PrintLabelsDialogProps> = ({
  open,
  onOpenChange,
  templateId,
  productName,
  templateCode,
  selectedInstances,
  initialMode = 'selected',
}) => {
  const [mode, setMode] = useState<PrintMode>(initialMode);
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!open) {
      setFromUnit('');
      setToUnit('');
      setError('');
      return;
    }
    setMode(initialMode);
    setError('');
  }, [open, initialMode]);

  React.useEffect(() => {
    if (!open || mode !== 'range') return;
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchAllInstancesForTemplate(templateId);
        if (cancelled || all.length === 0) return;
        setFromUnit(all[0].instance_number);
        setToUnit(all[all.length - 1].instance_number);
      } catch {
        /* range fields stay empty; user can enter manually */
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, templateId]);

  const handlePrint = async () => {
    setError('');
    setLoading(true);
    try {
      let instances: AssetInstance[] = [];
      if (mode === 'selected') {
        if (selectedInstances.length === 0) {
          throw new Error('Select at least one unit on the current page');
        }
        instances = selectedInstances;
      } else {
        const all = await fetchAllInstancesForTemplate(templateId);
        instances = mode === 'all' ? all : filterInstancesByRange(all, fromUnit, toUnit);
      }
      const items = toUnitLabelItems(instances, productName, templateCode);
      printUnitLabelPdf(items);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to print labels');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Print unit labels</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {(['selected', 'all', 'range'] as const).map((m) => (
            <label key={m} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="print-mode"
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              <span className="text-sm capitalize">
                {m === 'selected'
                  ? `Selected units (${selectedInstances.length})`
                  : m === 'all'
                    ? 'All units'
                    : 'Unit range'}
              </span>
            </label>
          ))}

          {mode === 'range' && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <Label htmlFor="from-unit">From unit</Label>
                <Input
                  id="from-unit"
                  value={fromUnit}
                  onChange={(e) => setFromUnit(e.target.value)}
                  placeholder="HB00001-0001"
                />
              </div>
              <div>
                <Label htmlFor="to-unit">To unit</Label>
                <Input
                  id="to-unit"
                  value={toUnit}
                  onChange={(e) => setToUnit(e.target.value)}
                  placeholder="HB00001-0050"
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" onClick={handlePrint} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Preparing…
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintLabelsDialog;
